import { useState, useCallback } from "react";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  signup: (email: string, password: string, displayName: string) => Promise<boolean>;
  login: (email: string, password: string) => Promise<boolean>;
  error: string | null;
  clearError: () => void;
  isLoading: boolean;
  initialMode?: "login" | "signup";
}

export function AuthModal({
  onClose,
  onSuccess,
  signup,
  login,
  error,
  clearError,
  isLoading,
  initialMode = "signup",
}: Props) {
  const [mode, setMode] = useState<"login" | "signup">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");

  const switchMode = useCallback(() => {
    setMode((m) => (m === "login" ? "signup" : "login"));
    clearError();
  }, [clearError]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      let ok: boolean;
      if (mode === "signup") {
        ok = await signup(email, password, displayName);
      } else {
        ok = await login(email, password);
      }
      if (ok) onSuccess();
    },
    [mode, email, password, displayName, signup, login, onSuccess],
  );

  return (
    <div className="auth-overlay" onClick={onClose}>
      <div className="auth-modal hud-panel" onClick={(e) => e.stopPropagation()}>
        <div className="auth-header">
          <h2 className="auth-title">
            {mode === "login" ? "WELCOME BACK" : "CREATE ACCOUNT"}
          </h2>
          <button className="auth-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {mode === "signup" && (
            <label className="auth-field">
              <span className="auth-label">CALL SIGN</span>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Captain Picard"
                required
                autoFocus={mode === "signup"}
              />
            </label>
          )}

          <label className="auth-field">
            <span className="auth-label">EMAIL</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="captain@starfleet.org"
              required
              autoFocus={mode === "login"}
            />
          </label>

          <label className="auth-field">
            <span className="auth-label">PASSWORD</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "signup" ? "8+ characters" : ""}
              required
              minLength={mode === "signup" ? 8 : undefined}
            />
          </label>

          {error && <div className="auth-error">{error}</div>}

          <button className="auth-submit" type="submit" disabled={isLoading}>
            {isLoading
              ? "STANDBY..."
              : mode === "login"
                ? "LOG IN"
                : "CREATE ACCOUNT"}
          </button>
        </form>

        <div className="auth-divider">
          <span>OR</span>
        </div>

        <button className="auth-google" disabled>
          <svg className="auth-google-icon" viewBox="0 0 24 24" width="16" height="16">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          CONTINUE WITH GOOGLE
          <span className="auth-badge">SOON</span>
        </button>

        <div className="auth-switch">
          {mode === "login" ? (
            <>
              New to Starbridge?{" "}
              <button className="auth-switch-btn" onClick={switchMode}>
                Create account
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button className="auth-switch-btn" onClick={switchMode}>
                Log in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
